import React, { useEffect, useState } from 'react';

import { Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { decode } from 'html-entities';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { Loading, Error } from 'components/Fetch';
import { getPath } from 'routes';
import { selectCurrentGid } from 'store/groups/reducer';

interface ITitleProps {
  title: string;
  template_id: string;
  carePlanTemplate?: ICarePlanTemplate;
  onFinishUpdate: () => void;
}

const Title = ({ title, template_id, carePlanTemplate, onFinishUpdate }: ITitleProps) => {
  const intl = useIntl();
  const [value, setValue] = useState(title);
  const gid = useAppSelector(selectCurrentGid);
  const navigate = useNavigate();
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';
  const ALREADY_IN_USE_MESSAGE = 'This template name is already in use';

  const [{ loading, error }, post] = useAxios(
    {
      url: `/clinician/2.0.0/CarePlanTemplate/${template_id}`,
      method: 'POST'
    },
    { manual: true }
  );

  const handleSave = async () => {
    const res = await post({
      url: '/clinician/2.0.0/CarePlanTemplate',
      data: {
        carePlanTemplate: {
          ...carePlanTemplate,
          template_description: value,
          ownership_uuid: gid,
          template_type: 'PRIVATE',
          template_id
        }
      }
    });
    onFinishUpdate();
    if (!template_id && res.data.template_id) {
      navigate(
        getPath().dashboard.groupManagement.groupManagementEditCarePlanTemplate(
          res.data.template_id
        )
      );
    }
  };

  useEffect(() => {
    setValue(title);
  }, [title]);

  useEffect(() => {
    if (error && error.response?.data.message === ALREADY_IN_USE_MESSAGE) {
      setValue(title);
    }
  }, [error, title]);

  return (
    <div className="mb-5">
      <h6 className="text-uppercase text-sm">{intl.formatMessage({ id: 'titleKey' })}</h6>

      <div className="d-flex align-items-baseline">
        <Form.Control
          value={isTemplateGlobal && value ? intl.formatMessage({ id: value }) : decode(value)}
          onChange={(e) => setValue(e.target.value)}
          className="w-30"
          onBlur={value ? handleSave : undefined}
          disabled={isTemplateGlobal}
          data-test="care-plan-title-input"
        />
        {error && (
          <Error
            className="ms-3"
            errorMessage={intl.formatMessage({ id: 'carePlanNameAlreadyInUseKey' })}
            dismissible={false}
          ></Error>
        )}
        {loading && <Loading className="ms-3" />}
      </div>
    </div>
  );
};

export default Title;
